import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import classnames from "classnames";

import { ACTION, SUBJECT } from "../../../../constants/permissions";
import { ActiveSubscriptionProps } from "./types";
import { getSubscriptionStatus } from "../../../../utils/common";
import ActiveSubscriptionDetail from "./components/ActiveSubscriptionDetail";
import PendingCancellationDetail from "./components/PendingCancellationDetail";
import PaymentFailureSubscriptionDetail from "./components/PaymentFailureSubscriptionDetail";
import PendingPaymentSubscriptionDetail from "./components/PendingPaymentSubscriptionDetail";
import { selectNextInvoiceData } from "../../../../store/common/selectors";
import { usePaymentType } from "../../../../hooks/use-payment-type";
import { PausedSubscriptionDetail } from "./components/PausedSubscriptionDetail/PausedSubscriptionDetail";
import { OnHoldSubscriptionDetail } from "./components/OnHoldSubscriptionDetail/OnHoldSubscriptionDetail";
import { TerminatedSubscriptionDetail } from "./components/TerminatedSubscriptionDetail/TerminatedSubscriptionDetail";
import { NotactiveSubscriptionDetail } from "./components/NotActiveSubscriptionDetail/NotactiveSubscriptionDetail";
import { useAbility } from "../../../../hooks";
import { SUBSCRIPTION_STATUS } from "../../../../utils/common/contants";
import { usePaymentIcon } from "../../../../hooks/use-payment-icon";

import "./styles.scss";

export const ActiveSubscription: FC<ActiveSubscriptionProps> = ({
  onCancelSubscription,
  onUncancelSubscription,
  data,
  billingInfo,
  onManageLicenses,
  isCorporate,
  isAccountOverviewTab,
  onResumePauseSubscription,
}) => {
  const { t } = useTranslation();
  const nextInvoice = useSelector(selectNextInvoiceData);
  const ability = useAbility();

  const subscriptionStatus = useMemo(() => {
    return getSubscriptionStatus(data);
  }, [data]);
  const paymentMethod = usePaymentType({
    billingInfo,
    spCode: data?.sp.sp_code,
  });

  const paymentMethodIconName = usePaymentIcon(billingInfo);

  const isCanBeCanceled = useMemo(() => {
    return (
      subscriptionStatus === SUBSCRIPTION_STATUS.active ||
      subscriptionStatus === SUBSCRIPTION_STATUS.paused
    );
  }, [subscriptionStatus]);
  const isCanBeUncanceled = useMemo(() => {
    return subscriptionStatus === SUBSCRIPTION_STATUS.pendingCancellation;
  }, [subscriptionStatus]);
  const isHasAccessDoActions =
    ability.can(ACTION.WRITE, SUBJECT.SUBSCRIPTION) ||
    ability.can(ACTION.WRITE, SUBJECT.BILLING);

  const renderSubscriptionData = () => {
    switch (subscriptionStatus) {
      case SUBSCRIPTION_STATUS.active:
        return (
          <ActiveSubscriptionDetail
            isCorporate={isCorporate}
            currentSubscription={data}
            nextInvoice={nextInvoice}
            paymentMethod={paymentMethod}
            onManageLicenses={onManageLicenses}
            isAccountOverviewTab={isAccountOverviewTab}
            paymentMethodIconName={paymentMethodIconName}
          />
        );
      case SUBSCRIPTION_STATUS.pendingCancellation:
        return (
          <PendingCancellationDetail
            isCorporate={isCorporate}
            currentSubscription={data}
            paymentMethod={paymentMethod}
            onManageLicenses={onManageLicenses}
            isAccountOverviewTab={isAccountOverviewTab}
            paymentMethodIconName={paymentMethodIconName}
          />
        );
      case SUBSCRIPTION_STATUS.expired:
        return <TerminatedSubscriptionDetail currentSubscription={data} />;
      case SUBSCRIPTION_STATUS.paymentFailure:
        return (
          <PaymentFailureSubscriptionDetail
            paymentMethod={paymentMethod}
            currentSubscription={data}
            paymentMethodIconName={paymentMethodIconName}
            isAccountOverviewTab={isAccountOverviewTab}
          />
        );
      case SUBSCRIPTION_STATUS.pendingPayment:
        return (
          <PendingPaymentSubscriptionDetail
            paymentMethod={paymentMethod}
            currentSubscription={data}
            paymentMethodIconName={paymentMethodIconName}
            isAccountOverviewTab={isAccountOverviewTab}
          />
        );
      case SUBSCRIPTION_STATUS.paused:
        return (
          <PausedSubscriptionDetail
            paymentMethod={paymentMethod}
            currentSubscription={data}
            onResumePauseSubscription={onResumePauseSubscription}
            paymentMethodIconName={paymentMethodIconName}
            isAccountOverviewTab={isAccountOverviewTab}
          />
        );
      case SUBSCRIPTION_STATUS.onHold:
        return (
          <OnHoldSubscriptionDetail
            paymentMethod={paymentMethod}
            currentSubscription={data}
            paymentMethodIconName={paymentMethodIconName}
            isAccountOverviewTab={isAccountOverviewTab}
          />
        );
      case SUBSCRIPTION_STATUS.notActive:
        return <NotactiveSubscriptionDetail currentSubscription={data} />;
      default:
        return null;
    }
  };

  return (
    <article
      className={classnames(
        isAccountOverviewTab && "ams-active-subscription--accountViewMode",
        "ams-active-subscription",
      )}
    >
      <div className="ams-active-subscription__header">
        <h1 className="ams-active-subscription__title">
          {t("subscriptionDetails")}
        </h1>
        {isCanBeCanceled && isHasAccessDoActions && !isAccountOverviewTab && (
          <>
            <button
              className="ams-active-subscription__link-button ams-active-subscription__link-button--desktop"
              onClick={onCancelSubscription}
            >
              {t("cancelSubscription")}
            </button>
          </>
        )}
        {isCanBeUncanceled && isHasAccessDoActions && !isAccountOverviewTab && (
          <>
            <button
              className="ams-active-subscription__link-button ams-active-subscription__link-button--desktop"
              onClick={onUncancelSubscription}
            >
              {t("uncancelSubscription")}
            </button>
          </>
        )}
      </div>

      {renderSubscriptionData()}

      {!isAccountOverviewTab && (
        <>
          {isCanBeCanceled && isHasAccessDoActions && (
            <>
              <button
                className="ams-active-subscription__link-button ams-active-subscription__link-button--mobile"
                onClick={onCancelSubscription}
              >
                {t("cancelSubscription")}
              </button>
            </>
          )}
          {isCanBeUncanceled && isHasAccessDoActions && (
            <>
              <button
                className="ams-active-subscription__link-button ams-active-subscription__link-button--mobile"
                onClick={onUncancelSubscription}
              >
                {t("uncancelSubscription")}
              </button>
            </>
          )}
        </>
      )}
    </article>
  );
};
